'use client';
import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface QRTypeData {
  type: string;
  count: number;
  pct: number;
}

interface AdminQRTypeChartProps {
  data: QRTypeData[];
}

const barColors = [
  '#2563EB',
  '#7C3AED',
  '#059669',
  '#D97706',
  '#0891B2',
  '#E11D48',
  '#EA580C',
  '#475569',
];

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: { value: number; payload: QRTypeData }[]; label?: string }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-card border border-border rounded-2xl px-4 py-3 shadow-elevated">
      <p className="font-semibold text-foreground" style={{ fontSize: '13px' }}>{label}</p>
      <p className="text-primary font-bold mt-1" style={{ fontSize: '18px', fontVariantNumeric: 'tabular-nums' }}>{d.count.toLocaleString()}</p>
      <p className="text-muted-foreground" style={{ fontSize: '12px' }}>{d.pct}% of all QR codes</p>
    </div>
  );
}

export default function AdminQRTypeChart({ data }: AdminQRTypeChartProps) {
  return (
    <div className="bg-card rounded-3xl border border-border shadow-card p-4 sm:p-5 h-full">
      <div className="flex items-center justify-between mb-4 sm:mb-5">
        <div>
          <h3 className="font-semibold text-foreground" style={{ fontSize: '15px' }}>QR Code Type Distribution</h3>
          <p className="text-muted-foreground mt-0.5" style={{ fontSize: '12px' }}>Total codes generated per type</p>
        </div>
        <span className="badge badge-blue">{data.reduce((a, d) => a + d.count, 0).toLocaleString()} total</span>
      </div>

      {/* Mobile: horizontal scrollable chart wrapper */}
      <div className="w-full overflow-x-auto -mx-1 px-1">
        <div style={{ minWidth: '320px' }}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data} margin={{ top: 4, right: 4, left: -24, bottom: 0 }} barSize={22}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="type"
                tick={{ fontSize: 10, fontWeight: 500, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-plus-jakarta-sans)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-plus-jakarta-sans)' }}
                axisLine={false}
                tickLine={false}
                width={32}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(37,99,235,0.04)', radius: 8 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {data.map((_, index) => (
                  <Cell key={`cell-${index + 1}`} fill={barColors[index % barColors.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Mobile legend pills */}
      <div className="mt-3 flex flex-wrap gap-2 sm:hidden">
        {data.slice(0, 4).map((d, i) => (
          <div key={`leg-${i}`} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: barColors[i % barColors.length] }} />
            <span className="text-muted-foreground" style={{ fontSize: '11px' }}>{d.type} ({d.pct}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}